import { useState, useCallback, useRef } from 'react';
import { Summary } from '~/types';
import { finalizeSession, getSummary } from '~/lib/api';

const POLL_INTERVAL_MS = 3000; // 3 seconds

export function useSummary() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<number | null>(null);

  /**
   * Start summary generation
   */
  const generate = useCallback(async (sessionId: string) => {
    try {
      setIsGenerating(true);
      setError(null);

      // Trigger summary generation
      await finalizeSession(sessionId);

      // Start polling for result
      pollSummary(sessionId);
    } catch (err: any) {
      setError(err.message || 'Failed to generate summary');
      setIsGenerating(false);
    }
  }, []);

  /**
   * Poll for summary result
   */
  const pollSummary = useCallback((sessionId: string) => {
    const poll = async () => {
      try {
        const result = await getSummary(sessionId);

        setSummary(result);

        // Stop polling if done or error
        if (result.status === 'done' || result.status === 'error') {
          if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsGenerating(false);

          if (result.status === 'error') {
            setError(result.error || 'Summary generation failed');
          }
        }
      } catch (err: any) {
        console.error('Failed to poll summary:', err);
      }
    };

    // Poll immediately
    poll();

    // Then poll periodically
    intervalRef.current = window.setInterval(poll, POLL_INTERVAL_MS);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setSummary(null);
    setIsGenerating(false);
    setError(null);
  }, []);

  return {
    summary,
    isGenerating,
    error,
    generate,
    reset,
  };
}
